import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useSidebar } from '../contexts/SidebarContext';
import {
  Project,
  ProjectStatus,
  ProjectStatusLabels,
  User,
  UserRole,
  UserRoleLabels,
} from '../types';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import { MemberSearchSelect } from '../components/common/MemberSearchSelect';
import { PhoneInput } from '../components/common/PhoneInput';
import { Avatar } from '../components/common/Avatar';
import { useToast } from '../contexts/ToastContext';
import { AppSectionHero, AppStatCard } from '../components/common/AppSurface';
import '../components/common/MemberSearchSelect.css';
import '../components/common/SharedInputs.css';
import {
  EditIcon, DeleteIcon, SaveIcon, CancelIcon, CopyIcon,
  FolderIcon, UsersIcon, SunIcon, InfoIcon, SettingsIcon,
} from '../components/icons/Icons';
import './SettingsPage.css';

// ==================== Editable Project Row ====================
const EditableProjectRow: React.FC<{
  project: Project;
  onSave: (id: string, updates: Partial<Project>) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  onDuplicate: (project: Project) => void;
  allUsers: User[];
  onToggleMember: (userId: string) => void | Promise<void>;
  teamMembers: string[];
}> = ({ project, onSave, onDelete, onDuplicate, allUsers, onToggleMember, teamMembers }) => {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [code, setCode] = useState(project.code);
  const [name, setName] = useState(project.name);
  const [client, setClient] = useState(project.client);
  const [status, setStatus] = useState<ProjectStatus>(project.status);

  const save = () => {
    onSave(project.id, { code, name, client, status });
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="editing-row">
        <td><input value={code} onChange={e => setCode(e.target.value)} className="inline-input mono" /></td>
        <td><input value={name} onChange={e => setName(e.target.value)} className="inline-input" /></td>
        <td><input value={client} onChange={e => setClient(e.target.value)} className="inline-input" /></td>
        <td>
          <select value={status} onChange={e => setStatus(e.target.value as ProjectStatus)} className="inline-select">
            <option value="PLANNING">Planning</option>
            <option value="ACTIVE">Active</option>
            <option value="ON_HOLD">On Hold</option>
            <option value="COMPLETED">Completed</option>
          </select>
        </td>
        <td className="members-cell">
          <MemberSearchSelect
            users={allUsers}
            selectedIds={teamMembers}
            onToggle={onToggleMember}
            multiSelect={true}
            placeholder="Select members"
          />
        </td>
        <td className="action-cell">
          <button className="action-icon-btn save" onClick={save} title="Save">
            <SaveIcon />
          </button>
          <button className="action-icon-btn cancel" onClick={() => setEditing(false)} title="Cancel">
            <CancelIcon />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="mono">{project.code}</td>
      <td>{project.name}</td>
      <td>{project.client}</td>
      <td>
        <span className="status-badge-sm" style={{ backgroundColor: project.status === 'ACTIVE' ? '#22c55e' : project.status === 'COMPLETED' ? '#3b82f6' : project.status === 'ON_HOLD' ? '#f59e0b' : '#a78bfa' }}>
          {ProjectStatusLabels[project.status]}
        </span>
      </td>
      <td className="members-cell">
        <span className="members-count-label">{teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}</span>
      </td>
      <td className="action-cell">
        {confirmDelete ? (
          <>
            <button className="action-icon-btn save" onClick={() => { onDelete(project.id); setConfirmDelete(false); }} title="Confirm Delete">
              <SaveIcon />
            </button>
            <button className="action-icon-btn cancel" onClick={() => setConfirmDelete(false)} title="Cancel">
              <CancelIcon />
            </button>
          </>
        ) : (
          <>
            <button className="action-icon-btn edit" onClick={() => setEditing(true)} title="Edit">
              <EditIcon size={15} />
            </button>
            <button className="action-icon-btn duplicate" onClick={() => onDuplicate(project)} title="Duplicate">
              <CopyIcon size={15} />
            </button>
            <button className="action-icon-btn delete" onClick={() => setConfirmDelete(true)} title="Delete">
              <DeleteIcon size={15} />
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

// ==================== Main Settings Page ====================
export const SettingsPage: React.FC = () => {
  const { currentUser, isProjectManager, users, addUser, updateUser, deleteUser, adminSetPassword } = useAuth();
  const { projects, updateProject, deleteProject, addProject, duplicateProject, addProjectMember, removeProjectMember, allUsers } = useProjects();
  const { theme, toggleTheme } = useSidebar();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'projects' | 'users' | 'appearance' | 'system'>('projects');

  // Project form state
  const [addingProject, setAddingProject] = useState(false);
  const [newProjCode, setNewProjCode] = useState('');
  const [newProjName, setNewProjName] = useState('');
  const [newProjClient, setNewProjClient] = useState('');

  // User management state
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserRole, setEditUserRole] = useState<UserRole>('TECHNICIAN');
  const [editUserPhone, setEditUserPhone] = useState('');
  const [confirmDelUser, setConfirmDelUser] = useState<string | null>(null);

  // Add User form state
  const [addingUser, setAddingUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('TECHNICIAN');
  const [newUserPhone, setNewUserPhone] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [addUserError, setAddUserError] = useState('');

  // Change password modal state
  const [pwdModalUserId, setPwdModalUserId] = useState<string | null>(null);
  const [pwdModalUserName, setPwdModalUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');

  const handleAddUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword) {
      setAddUserError('Nombre, email y contraseña son requeridos.');
      return;
    }
    if (newUserPassword.length < 8) {
      setAddUserError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setAddUserError('');
    const result = await addUser({
      name: newUserName.trim(),
      email: newUserEmail.trim(),
      role: newUserRole,
      password: newUserPassword,
      avatarColor: '',
      allowedSections: newUserRole === 'PROJECT_MANAGER'
        ? ['dashboard', 'workspace', 'my-tasks', 'team', 'reports', 'settings', 'calendar']
        : ['my-tasks', 'calendar'],
      notificationPhone: newUserPhone || undefined,
    });
    if (!result.success) {
      setAddUserError(result.error || 'No se pudo crear el usuario.');
      return;
    }
    showToast('User created successfully', 'success');
    setAddingUser(false);
    setNewUserName('');
    setNewUserEmail('');
    setNewUserRole('TECHNICIAN');
    setNewUserPhone('');
    setNewUserPassword('');
  };

  const openPwdModal = (id: string, name: string) => {
    setPwdModalUserId(id);
    setPwdModalUserName(name);
    setNewPassword('');
    setConfirmPassword('');
    setPwdError('');
    setPwdSuccess('');
  };

  const closePwdModal = () => {
    setPwdModalUserId(null);
    setNewPassword('');
    setConfirmPassword('');
    setPwdError('');
    setPwdSuccess('');
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setPwdError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError('Las contraseñas no coinciden.');
      return;
    }
    setPwdError('');
    const result = await adminSetPassword(pwdModalUserId!, newPassword);
    if (result.success) {
      setPwdSuccess('Contraseña actualizada correctamente.');
      showToast('Password reset successfully', 'success');
      setTimeout(() => closePwdModal(), 1500);
    } else {
      setPwdError(result.error || 'Error al actualizar la contraseña.');
    }
  };

  const handleAddProject = async () => {
    if (!newProjCode.trim() || !newProjName.trim()) return;
    await addProject({
      name: newProjName.trim(),
      code: newProjCode.trim(),
      description: '',
      client: newProjClient.trim(),
      status: 'PLANNING',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      teamMembers: [],
    });
    showToast('Project created successfully', 'success');
    setNewProjCode('');
    setNewProjName('');
    setNewProjClient('');
    setAddingProject(false);
  };

  const roleLabel = (role: UserRole) => UserRoleLabels[role] || role;
  const getUserLabel = (userId: string) => allUsers.find(user => user.id === userId)?.name || 'Member';

  return (
    <div className="settings-page">
      <AppSectionHero
        icon={<SettingsIcon size={28} />}
        title="Settings"
        subtitle="Manage projects, users, appearance, and system information."
        aside={
          <>
            <AppStatCard icon={<FolderIcon size={20} />} label="Projects" value={projects.length} caption="Registered in the system" tone="blue" />
            <AppStatCard icon={<UsersIcon size={20} />} label="Users" value={users.length} caption="Available accounts" tone="mint" />
          </>
        }
      />

      <div className="settings-tabs app-surface-chip-group" role="tablist" aria-label="Settings sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'projects'}
          className={`app-surface-chip-btn settings-tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          <FolderIcon size={16} />
          Projects
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'users'}
          className={`app-surface-chip-btn settings-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <UsersIcon size={16} />
          Users
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'appearance'}
          className={`app-surface-chip-btn settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          <SunIcon size={16} />
          Appearance
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'system'}
          className={`app-surface-chip-btn settings-tab ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          <InfoIcon size={16} />
          System
        </button>
      </div>

      <div className="settings-content">
      {/* ===== Projects Section ===== */}
      {activeTab === 'projects' && isProjectManager && (
        <div className="settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">
              <FolderIcon />
              Projects
            </h3>
            <button className="add-btn" onClick={() => setAddingProject(!addingProject)}>+ Add Project</button>
          </div>

          {addingProject && (
            <div className="new-form-row">
              <input placeholder="Code" value={newProjCode} onChange={e => setNewProjCode(e.target.value)} className="inline-input mono" />
              <input placeholder="Project Name" value={newProjName} onChange={e => setNewProjName(e.target.value)} className="inline-input" />
              <input placeholder="Client" value={newProjClient} onChange={e => setNewProjClient(e.target.value)} className="inline-input" />
              <button className="action-icon-btn save" onClick={handleAddProject} title="Add">
                <SaveIcon />
              </button>
              <button className="action-icon-btn cancel" onClick={() => setAddingProject(false)} title="Cancel">
                <CancelIcon />
              </button>
            </div>
          )}

          <div className="settings-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Members</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <EditableProjectRow
                    key={p.id}
                    project={p}
                    onSave={async (id, updates) => {
                      await updateProject(id, updates);
                      showToast('Project updated successfully', 'success');
                    }}
                    onDelete={async (id) => {
                      await deleteProject(id);
                      showToast('Project deleted', 'info');
                    }}
                    onDuplicate={(p) => duplicateProject(p)}
                    allUsers={allUsers}
                    onToggleMember={async (userId) => {
                      if (p.teamMembers.includes(userId)) {
                        await removeProjectMember(p.id, userId);
                        showToast(`${getUserLabel(userId)} removed from project`, 'info');
                      } else {
                        await addProjectMember(p.id, userId);
                        showToast(`${getUserLabel(userId)} added to project`, 'success');
                      }
                    }}
                    teamMembers={p.teamMembers}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== User Management ===== */}
      {activeTab === 'users' && isProjectManager && (
        <div className="settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">
              <UsersIcon />
              User Management
            </h3>
            <button className="add-btn" onClick={() => { setAddingUser(!addingUser); setAddUserError(''); }}>
              + Add User
            </button>
          </div>

          {addingUser && (
            <div className="new-form-row" style={{ flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                placeholder="Full Name"
                value={newUserName}
                onChange={e => setNewUserName(e.target.value)}
                className="inline-input"
              />
              <input
                placeholder="Email"
                type="email"
                value={newUserEmail}
                onChange={e => setNewUserEmail(e.target.value)}
                className="inline-input"
              />
              <input
                placeholder="Password (min. 8 chars)"
                type="password"
                value={newUserPassword}
                onChange={e => setNewUserPassword(e.target.value)}
                className="inline-input"
              />
              <select
                value={newUserRole}
                onChange={e => setNewUserRole(e.target.value as UserRole)}
                className="inline-select"
              >
                {(Object.entries(UserRoleLabels) as [UserRole, string][]).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              <PhoneInput
                value={newUserPhone}
                onChange={setNewUserPhone}
                placeholder="+1 (555) 123-4567"
                className="inline-input"
              />
              {addUserError && (
                <span className="settings-inline-error" style={{ width: '100%' }}>{addUserError}</span>
              )}
              <button className="action-icon-btn save" onClick={handleAddUser} title="Create user">
                <SaveIcon />
              </button>
              <button className="action-icon-btn cancel" onClick={() => { setAddingUser(false); setAddUserError(''); }} title="Cancel">
                <CancelIcon />
              </button>
            </div>
          )}

          <div className="settings-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isLocalEmail = u.email?.endsWith('@rae-pm.local');
                  const displayEmail = isLocalEmail ? (u.notificationEmail || '') : u.email;

                  return editUserId === u.id ? (
                    <tr key={u.id} className="editing-row">
                      <td>
                        <input
                          value={editUserName}
                          onChange={e => setEditUserName(e.target.value)}
                          className="inline-input"
                        />
                      </td>
                      <td>
                        <select
                          value={editUserRole}
                          onChange={e => setEditUserRole(e.target.value as UserRole)}
                          className="inline-select"
                        >
                          {(Object.entries(UserRoleLabels) as [UserRole, string][]).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <span className="user-email-readonly">{u.email}</span>
                      </td>
                      <td>
                        <PhoneInput
                          value={editUserPhone}
                          onChange={setEditUserPhone}
                          placeholder="+1 (555) 123-4567"
                          className="inline-input"
                        />
                      </td>
                      <td>{u.createdAt || '\u2014'}</td>
                      <td className="action-cell">
                        <button
                          className="action-icon-btn save"
                          onClick={async () => {
                            await updateUser(u.id, {
                              name: editUserName,
                              role: editUserRole,
                              notificationPhone: editUserPhone || undefined,
                            });
                            showToast('User updated successfully', 'success');
                            setEditUserId(null);
                          }}
                          title="Save"
                        >
                          <SaveIcon />
                        </button>
                        <button className="action-icon-btn cancel" onClick={() => setEditUserId(null)} title="Cancel">
                          <CancelIcon />
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={u.id}>
                      <td className="address-label">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Avatar user={u} size={28} className="settings-user-avatar" />
                          {u.name}
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge ${u.role.toLowerCase()}`}>
                          {roleLabel(u.role)}
                        </span>
                      </td>
                      <td className="user-email-cell">
                        {displayEmail ? (
                          <span className="user-email-value">{displayEmail}</span>
                        ) : (
                          <span className="user-email-empty">{isLocalEmail ? 'No email set' : '\u2014'}</span>
                        )}
                        {isLocalEmail && u.notificationEmail && (
                          <span className="user-email-local-tag" title={`Auth: ${u.email}`}>custom</span>
                        )}
                      </td>
                      <td>
                        {u.notificationPhone ? (
                          <span className="user-phone-value">{u.notificationPhone}</span>
                        ) : '\u2014'}
                      </td>
                      <td className="address-text">{u.createdAt || '\u2014'}</td>
                      <td className="action-cell">
                        {confirmDelUser === u.id ? (
                          <>
                            <button
                              className="action-icon-btn save"
                              onClick={async () => {
                                await deleteUser(u.id);
                                showToast('User deleted', 'info');
                                setConfirmDelUser(null);
                              }}
                              title="Confirm Delete"
                            >
                              <SaveIcon />
                            </button>
                            <button className="action-icon-btn cancel" onClick={() => setConfirmDelUser(null)} title="Cancel">
                              <CancelIcon />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="action-icon-btn edit"
                              onClick={() => {
                                setEditUserId(u.id);
                                setEditUserName(u.name);
                                setEditUserRole(u.role);
                                setEditUserPhone(u.notificationPhone || '');
                              }}
                              title="Edit"
                            >
                              <EditIcon size={15} />
                            </button>
                            {u.id !== currentUser?.id && (
                              <button
                                className="action-icon-btn edit"
                                title="Change password"
                                onClick={() => openPwdModal(u.id, u.name)}
                              >
                                &#128274;
                              </button>
                            )}
                            <button className="action-icon-btn delete" onClick={() => setConfirmDelUser(u.id)} title="Delete">
                              <DeleteIcon size={15} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Appearance ===== */}
      {activeTab === 'appearance' && (
      <div className="settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">
            <SunIcon size={16} />
            Appearance
          </h3>
        </div>
        <div className="info-list">
          <div className="info-row">
            <span className="info-label">Theme</span>
            <button className="settings-theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? '\u2600\uFE0F Switch to Light' : '\uD83C\uDF19 Switch to Dark'}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* ===== System Information ===== */}
      {activeTab === 'system' && (
      <div className="settings-card">
        <h3 className="settings-card-title">
          <InfoIcon />
          System Information
        </h3>
        <div className="info-list">
          <div className="info-row"><span className="info-label">Application</span><span className="info-value">RAE Project Management</span></div>
          <div className="info-row"><span className="info-label">Version</span><span className="info-value">1.0.0</span></div>
          <div className="info-row">
            <span className="info-label">Mode</span>
            <span className="info-value">
              {isSupabaseConfigured()
                ? <span className="settings-supabase-badge">Online</span>
                : <span className="settings-mock-badge">Offline</span>
              }
            </span>
          </div>
          <div className="info-row"><span className="info-label">User</span><span className="info-value">{currentUser?.name} ({currentUser?.role ? roleLabel(currentUser.role) : 'User'})</span></div>
        </div>
      </div>
      )}
      </div>

      {/* ===== Change Password Modal ===== */}
      {pwdModalUserId && (
        <div className="settings-modal-overlay" onClick={closePwdModal}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>Change Password</h3>
              <button className="settings-modal-close" onClick={closePwdModal}>&times;</button>
            </div>
            <p className="settings-modal-subtitle">User: <strong>{pwdModalUserName}</strong></p>
            <div className="settings-modal-body">
              <input
                type="password"
                placeholder="New password (min. 8 chars)"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="inline-input"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="inline-input"
                onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
              />
              {pwdError && <p className="settings-inline-error" style={{ marginTop: '0.25rem' }}>{pwdError}</p>}
              {pwdSuccess && <p className="settings-pwd-success">{pwdSuccess}</p>}
            </div>
            <div className="settings-modal-footer">
              <button className="action-icon-btn cancel" onClick={closePwdModal}>Cancel</button>
              <button className="action-icon-btn save" onClick={handleSetPassword}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
